import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Header, Modal } from 'semantic-ui-react';

import { AdminRole, adminRoles, Affiliation } from '@/types/affiliation';
import { getFullName } from '@/utils/name';

type Props = {
  children?: never;
  affiliation: Affiliation;
  onClickUpdateRole: (adminId: number, role: AdminRole) => void;
};

export const AssignRole: React.FC<Props> = (props) => {
  const { affiliation, onClickUpdateRole } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [targetRole, setTargetRole] = useState<AdminRole>();

  const close = () => setIsOpen(false);
  const confirmAssign = (role: AdminRole) => {
    setIsOpen(true);
    setTargetRole(role);
  };

  const { admin, role: adminRole } = affiliation;
  const adminFullName = getFullName(admin.firstName, admin.lastName);
  const displayName = adminFullName || admin.email;

  const { t } = useTranslation();

  return (
    <>
      <Dropdown
        data-test="roles"
        options={adminRoles.map((role, index) => ({
          'data-test': `${role}-role-${admin.email}`,
          active: affiliation.role === role,
          key: index,
          value: role,
          text: t(`models.affiliation.roleType.${role}.name`),
          onClick: () => {
            if (affiliation.role !== role) {
              confirmAssign(role);
            }
          },
        }))}
        defaultValue={affiliation.role}
        selection={true}
        text={
          affiliation.role
            ? t(`models.affiliation.roleType.${adminRole}.name`)
            : ''
        }
      />
      <Modal dimmer="blurring" open={isOpen}>
        <Header content={t('ui.confirm.assignRole.title')} />
        <Modal.Content>
          <div>
            {t(`ui.confirm.assignRole.body`, {
              name: displayName,
              honorific: adminFullName ? t('ui.honorific') : ' ',
              roleType: targetRole
                ? t(`models.affiliation.roleType.${targetRole}.name`)
                : t(`models.affiliation.roleType.member.name`),
            })}
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button data-test="cancel" onClick={close}>
            {t('ui.action.cancel')}
          </Button>
          <Button
            primary={true}
            data-test="execute"
            onClick={() => {
              if (targetRole) {
                onClickUpdateRole(admin.id, targetRole);
              }
              close();
            }}
          >
            {t('ui.action.change')}
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};
